import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@et/environment';
import { getDocPrepServiceDomain } from '@et/utils';
import {
  UserSubsetsData,
  SubsetsResponse,
  Subset,
  UserSubsets,
  AddSubsetsDto,
  UpsertSubsetsResponse,
  UpdateSubsetsDto,
} from '@et/typings';
import { catchError, map, Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SubsetsService {
  private readonly baseUrl = getDocPrepServiceDomain();

  private http = inject(HttpClient);

  /**
   * Retrieves subsets for a specific company.
   *
   * This method sends a GET request to fetch subsets associated with the provided company ID.
   *
   * @param {string} companyId - The ID of the company for which to retrieve subsets.
   * @returns {Observable<Subset[]>} An observable containing the list of subsets.
   */
  getSubsets(companyId: string): Observable<Subset[]> {
    return this.http
      .get<SubsetsResponse>(`${this.baseUrl}/api/pageSubset/${companyId}`)
      .pipe(
        map((res) => res.data.pageSubset),
        catchError((err) => {
          if (err.status === 404) return of([]);
          throw err;
        }),
      );
  }

  /**
   * Deletes one or more subsets for a specific company.
   *
   * This method sends a DELETE request to remove the provided subset IDs from the company.
   *
   * @param {string} companyId - The ID of the company whose subsets are to be deleted.
   * @param {string[]} subsetIds - The array of subset IDs to delete.
   * @returns {Observable<any>} An observable representing the server's response.
   */
  deleteSubsets(companyId: string, subsetIds: string[]): Observable<any> {
    return this.http.delete(`${this.baseUrl}/api/pageSubset/${companyId}`, {
      body: subsetIds,
    });
  }

  /**
   * Adds new subsets to a company.
   *
   * This method sends a POST request to add the provided subsets to the company.
   *
   * @param {AddSubsetsDto} addSubsetsDto - The DTO containing the subsets to add.
   * @returns {Observable<Subset>} An observable containing the server's response.
   */
  addSubsets(addSubsetsDto: AddSubsetsDto): Observable<Subset[]> {
    return this.http
      .post<UpsertSubsetsResponse>(
        `${this.baseUrl}/api/pageSubset/add`,
        addSubsetsDto,
      )
      .pipe(map((res) => JSON.parse(res.data) as Subset[]));
  }

  /**
   * Updates one or more subsets for a specific company.
   *
   * This method sends a PATCH request to update the provided subsets for the company.
   *
   * @param {string} companId - The ID of the company whose subsets are to be updated.
   * @param {UpdateSubsetsDto} subsetsDto - The DTO containing the subsets to update.
   * @returns {Observable<UpsertSubsetsResponse>} An observable containing the server's response.
   */
  updateSubsets(
    companId: string,
    subsetsDto: UpdateSubsetsDto,
  ): Observable<UpsertSubsetsResponse> {
    const params = new HttpParams().append('companyId', companId);
    return this.http.patch<UpsertSubsetsResponse>(
      `${this.baseUrl}/api/pageSubset/update`,
      subsetsDto,
      { params },
    );
  }

  /**
   * Retrieves subsets assigned to a specific user.
   *
   * This method sends a GET request to fetch subsets associated with the provided user ID.
   * If no subsets are found (404 error), it returns an empty array.
   *
   * @param {string} userId - The ID of the user for which to retrieve subsets.
   * @returns {Observable<UserSubsets[]>} An observable containing the list of user subsets.
   */
  getUserSubsets(userId: string): Observable<UserSubsets[]> {
    return this.http
      .get<UserSubsetsData>(
        `${this.baseUrl}/api/pageSubset/UserSubset/${userId}`,
      )
      .pipe(
        map((res) => res.pageSubset),
        catchError((err) => {
          if (err.status === 404) {
            return of([]);
          }
          throw err;
        }),
      );
  }

  /**
   * Assigns subsets to a user.
   *
   * This method sends a PATCH request to assign the provided subsets to a user.
   *
   * @param {UserSubsetsData} body - The data containing subsets to assign to the user.
   * @returns {Observable<object>} An observable representing the server's response.
   */
  addUserSubsets(body: UserSubsetsData): Observable<object> {
    return this.http.patch(`${this.baseUrl}/api/pageSubset/patchUser`, body);
  }

  /**
   * Removes a subset from a user.
   *
   * This method sends a DELETE request to remove the provided subset from a user.
   *
   * @param {UserSubsetsData} body - The data containing the subset to remove from the user.
   * @returns {Observable<object>} An observable representing the server's response.
   */
  deleteUserSubset(body: UserSubsetsData): Observable<object> {
    return this.http.delete(`${this.baseUrl}/api/pageSubset/UserSubset`, {
      body,
    });
  }
}
